import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  CheckCircle2,
  Clock,
  XCircle,
  Building2,
  Layers,
  FileText,
  BookOpen,
  Eye,
  Download,
  ExternalLink,
  Coins,
  AlertTriangle,
  RefreshCw,
  Sliders,
  Users,
  User,
  Search,
  X,
  Copy,
  Check,
  Calendar,
  MessageSquare,
  GraduationCap,
} from "lucide-react";
import * as api from "@/src/api/sdk.gen";
import { formatApiErrorMessage } from "@/src/lib/error-utils";

export interface AdminCustomCourseDocument {
  id: number;
  uuid: string;
  organization_id: number;
  workspace_id?: number | null;
  title: string;
  document_type: string;
  file_url: string;
  file_size_bytes?: number | null;
  description?: string | null;
  status?: string;
  chunks_count?: number;
  extracted_text?: string | null;
  created_at?: string;
}

export interface AdminCustomCourseRequest {
  id: number;
  uuid: string;
  workspace_id: number;
  workspace_name?: string | null;
  workspace_slug?: string | null;
  organization_id: number;
  organization_name?: string | null;
  requested_by: number;
  requester_name?: string | null;
  requester_email?: string | null;
  course_title: string;
  target_audience: string;
  learning_objectives: string;
  suggested_modules: string[];
  reference_document_ids: number[];
  reference_documents?: AdminCustomCourseDocument[];
  image_url?: string | null;
  status: string;
  admin_feedback?: string | null;
  generated_course_id?: number | null;
  organization_credit_balance?: number;
  created_at: string;
  updated_at: string;
}

interface CustomCourseRequestsClientProps {
  requests?: AdminCustomCourseRequest[];
  loading?: boolean;
  onRefresh?: () => Promise<void>;
}

export function CustomCourseRequestsClient({
  requests: propRequests,
  loading: propLoading,
  onRefresh,
}: CustomCourseRequestsClientProps) {
  // Local state if not managed externally
  const [internalRequests, setInternalRequests] = useState<AdminCustomCourseRequest[]>([]);
  const [internalLoading, setInternalLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const requests = propRequests !== undefined ? propRequests : internalRequests;
  const loading = propLoading !== undefined ? propLoading : internalLoading;

  // Filters & Search
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);

  // Modals state
  const [activeCourseReqModal, setActiveCourseReqModal] = useState<AdminCustomCourseRequest | null>(null);
  const [detailsModalReq, setDetailsModalReq] = useState<AdminCustomCourseRequest | null>(null);
  const [previewDocModal, setPreviewDocModal] = useState<AdminCustomCourseDocument | null>(null);
  const [adminFeedbackInput, setAdminFeedbackInput] = useState("");
  const [reviewStatusInput, setReviewStatusInput] = useState("in_review");
  const [updatingCourseReq, setUpdatingCourseReq] = useState(false);

  // Reject Modal State
  const [rejectModalCourseReq, setRejectModalCourseReq] = useState<AdminCustomCourseRequest | null>(null);
  const [courseRejectionFeedback, setCourseRejectionFeedback] = useState("");
  const [rejectingCourseReq, setRejectingCourseReq] = useState(false);

  // AI Generation Modal State
  const [generateModalReq, setGenerateModalReq] = useState<AdminCustomCourseRequest | null>(null);
  const [synthesizingOutline, setSynthesizingOutline] = useState(false);
  const [outlineText, setOutlineText] = useState("");
  const [plannedLessons, setPlannedLessons] = useState<number | null>(null);
  const [creditsRequired, setCreditsRequired] = useState<number | null>(null);
  const [debitCredits, setDebitCredits] = useState(true);
  const [customInstructions, setCustomInstructions] = useState("");
  const [targetModules, setTargetModules] = useState<number>(3);
  const [lessonsPerModule, setLessonsPerModule] = useState<number>(3);
  const [quizzesPerModule, setQuizzesPerModule] = useState<number>(1);
  const [generationError, setGenerationError] = useState<string | null>(null);

  // Copy feedback state for extracted document text
  const [copiedText, setCopiedText] = useState(false);

  const formatFileSize = (bytes?: number | null) => {
    if (!bytes) return "N/A";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const resolveAdminDocUrl = (url: string) => {
    if (!url) return "#";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    const rawApiBase = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || "";
    const cleanedBase = rawApiBase.replace(/\/api\/v1\/?$/, "");
    return `${cleanedBase}${url}`;
  };

  const fetchInternalRequests = async () => {
    try {
      setInternalLoading(true);
      const { data, error } = await api.listAllCustomCourseRequestsApiV1AdminCustomCourseRequestsGet();
      if (!error && data) {
        setInternalRequests((data.requests || []) as unknown as AdminCustomCourseRequest[]);
      }
    } catch (err) {
      console.error("Failed to load custom course requests:", err);
    } finally {
      setInternalLoading(false);
    }
  };

  useEffect(() => {
    if (propRequests === undefined) {
      void fetchInternalRequests();
    }
  }, [propRequests]);

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      if (onRefresh) {
        await onRefresh();
      } else {
        await fetchInternalRequests();
      }
    } finally {
      setRefreshing(false);
    }
  };

  // KPI Calculations
  const kpis = useMemo(() => {
    const total = requests.length;
    const submitted = requests.filter((r) => r.status === "submitted").length;
    const inReview = requests.filter((r) => r.status === "in_review").length;
    const generating = requests.filter((r) => r.status === "generating").length;
    const published = requests.filter((r) => r.status === "published").length;
    const rejected = requests.filter((r) => r.status === "rejected").length;
    const needsReview = submitted + inReview;

    return { total, submitted, inReview, generating, published, rejected, needsReview };
  }, [requests]);

  // Filtered Requests
  const filteredRequests = useMemo(() => {
    return requests.filter((r) => {
      // Status filtering
      if (statusFilter === "needs_review") {
        if (r.status !== "submitted" && r.status !== "in_review") return false;
      } else if (statusFilter !== "all" && r.status !== statusFilter) {
        return false;
      }

      // Search filtering
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase().trim();
      return (
        r.course_title.toLowerCase().includes(q) ||
        (r.organization_name || "").toLowerCase().includes(q) ||
        (r.workspace_name || "").toLowerCase().includes(q) ||
        (r.requester_name && r.requester_name.toLowerCase().includes(q)) ||
        (r.requester_email && r.requester_email.toLowerCase().includes(q)) ||
        r.target_audience.toLowerCase().includes(q) ||
        (r.learning_objectives && r.learning_objectives.toLowerCase().includes(q))
      );
    });
  }, [requests, statusFilter, searchQuery]);

  // Document Preview Handler
  const handlePreviewDocument = async (reqId: number, doc: AdminCustomCourseDocument) => {
    setPreviewDocModal(doc);
    setCopiedText(false);
    if (!doc.extracted_text) {
      try {
        const { data } = await api.getCustomCourseRequestDocumentDetailApiV1AdminCustomCourseRequestsRequestIdDocumentsDocumentIdGet({
          path: { request_id: reqId, document_id: doc.id },
        });
        if (data) {
          setPreviewDocModal((prev) => (prev && prev.id === doc.id ? (data as unknown as AdminCustomCourseDocument) : prev));
        }
      } catch (err) {
        console.error("Failed to fetch document detail:", err);
      }
    }
  };

  const handleCopyText = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedText(true);
    setTimeout(() => setCopiedText(false), 2000);
  };

  // AI Outline Generation
  const handleOpenGenerateModal = (req: AdminCustomCourseRequest) => {
    setGenerateModalReq(req);
    setOutlineText("");
    setPlannedLessons(null);
    setCreditsRequired(null);
    setDebitCredits(true);
    setCustomInstructions("");
    setTargetModules(req.suggested_modules?.length ? Math.min(req.suggested_modules.length, 6) : 3);
    setLessonsPerModule(3);
    setQuizzesPerModule(1);
    setGenerationError(null);
  };

  const handleSynthesizeOutline = async () => {
    if (!generateModalReq) return;
    try {
      setSynthesizingOutline(true);
      setGenerationError(null);
      const { data, error } = await api.synthesizeCustomCourseOutlineApiV1AdminCustomCourseRequestsRequestIdSynthesizeOutlinePost({
        path: { request_id: generateModalReq.id },
        body: {
          custom_instructions: customInstructions.trim() || undefined,
          target_modules: targetModules,
          lessons_per_module: lessonsPerModule,
          quizzes_per_module: quizzesPerModule,
        } as any,
      });
      if (error || !data) {
        setGenerationError(formatApiErrorMessage(error, "Failed to synthesize outline with AI."));
        return;
      }
      setOutlineText(data.outline_text);
      setPlannedLessons(data.total_lessons);
      setCreditsRequired(data.estimated_credits_required);
      if (typeof data.organization_credit_balance === "number") {
        setGenerateModalReq((prev) =>
          prev ? { ...prev, organization_credit_balance: data.organization_credit_balance } : prev
        );
      }
    } catch (err: any) {
      setGenerationError(formatApiErrorMessage(err, "Error generating AI outline."));
    } finally {
      setSynthesizingOutline(false);
    }
  };

  const handleConfirmGenerate = async () => {
    if (!generateModalReq) return;
    try {
      setActionLoadingId(generateModalReq.id);
      setGenerationError(null);
      const { error } = await api.triggerCustomCourseGenerationApiV1AdminCustomCourseRequestsRequestIdGeneratePost({
        path: { request_id: generateModalReq.id },
        body: {
          outline_text: outlineText.trim() || undefined,
          debit_org_credits: debitCredits,
          custom_instructions: customInstructions.trim() || undefined,
        },
      });
      if (error) {
        setGenerationError(formatApiErrorMessage(error, "Failed to trigger AI course generation."));
        return;
      }
      setGenerateModalReq(null);
      await handleRefresh();
      alert("AI course generation dispatched! Background workers are generating grounded lessons.");
    } catch (err: any) {
      setGenerationError(formatApiErrorMessage(err, "Unexpected error dispatching generation."));
    } finally {
      setActionLoadingId(null);
    }
  };

  const handlePublishCourse = async (reqId: number) => {
    try {
      setActionLoadingId(reqId);
      const { error } = await api.publishCustomCourseToWorkspaceApiV1AdminCustomCourseRequestsRequestIdPublishPost({
        path: { request_id: reqId },
        body: {},
      });
      if (!error) {
        await handleRefresh();
        alert("Course published and automatically assigned to workspace!");
      } else {
        const err = error as any;
        alert(`Failed to publish course: ${err?.detail || "Unknown error"}`);
      }
    } catch (err) {
      console.error("Error publishing course:", err);
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleSaveCourseReview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeCourseReqModal) return;
    try {
      setUpdatingCourseReq(true);
      const { error } = await api.updateCustomCourseRequestApiV1AdminCustomCourseRequestsRequestIdPatch({
        path: { request_id: activeCourseReqModal.id },
        body: {
          status: reviewStatusInput,
          admin_feedback: adminFeedbackInput.trim() || undefined,
        },
      });
      if (!error) {
        setActiveCourseReqModal(null);
        await handleRefresh();
      } else {
        const err = error as any;
        alert(`Failed to update review: ${err?.detail || "Unknown error"}`);
      }
    } catch (err) {
      console.error("Error saving review:", err);
    } finally {
      setUpdatingCourseReq(false);
    }
  };

  const handleRejectCourseRequestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rejectModalCourseReq) return;
    try {
      setRejectingCourseReq(true);
      const { error } = await api.updateCustomCourseRequestApiV1AdminCustomCourseRequestsRequestIdPatch({
        path: { request_id: rejectModalCourseReq.id },
        body: {
          status: "rejected",
          admin_feedback: courseRejectionFeedback.trim() || undefined,
        },
      });
      if (!error) {
        setRejectModalCourseReq(null);
        setCourseRejectionFeedback("");
        await handleRefresh();
      } else {
        const err = error as any;
        alert(`Failed to reject custom course request: ${err?.detail || "Unknown error"}`);
      }
    } catch (err) {
      console.error("Error rejecting custom course request:", err);
    } finally {
      setRejectingCourseReq(false);
    }
  };

  const renderStatusBadge = (status: string) => {
    switch (status) {
      case "published":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            Published
          </span>
        );
      case "generating":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-purple-50 text-purple-700 border border-purple-200">
            <Sparkles className="w-3.5 h-3.5 text-purple-600 animate-spin" />
            Generating AI Course
          </span>
        );
      case "in_review":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-blue-50 text-blue-700 border border-blue-200">
            <Eye className="w-3.5 h-3.5 text-blue-600" />
            In Review
          </span>
        );
      case "rejected":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-rose-50 text-rose-700 border border-rose-200">
            <XCircle className="w-3.5 h-3.5 text-rose-600" />
            Rejected
          </span>
        );
      case "submitted":
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-amber-50 text-amber-700 border border-amber-200">
            <Clock className="w-3.5 h-3.5 text-amber-600" />
            Submitted
          </span>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* 1. Summary KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Requests */}
        <button
          type="button"
          onClick={() => setStatusFilter("all")}
          className={`text-left p-4 rounded-2xl bg-white border transition-all duration-200 shadow-sm hover:shadow-md ${
            statusFilter === "all" ? "border-indigo-500 ring-2 ring-indigo-500/20" : "border-slate-200 hover:border-slate-300"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Total Requests</span>
            <div className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-2xl font-black text-slate-900">{kpis.total}</span>
            <span className="text-xs text-slate-500 font-medium">all time</span>
          </div>
        </button>

        {/* Needs Review */}
        <button
          type="button"
          onClick={() => setStatusFilter("needs_review")}
          className={`text-left p-4 rounded-2xl bg-white border transition-all duration-200 shadow-sm hover:shadow-md ${
            statusFilter === "needs_review" ? "border-amber-500 ring-2 ring-amber-500/20" : "border-slate-200 hover:border-slate-300"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Needs Review</span>
            <div className="w-9 h-9 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
              <Clock className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-2xl font-black text-amber-600">{kpis.needsReview}</span>
            <span className="text-xs text-slate-500 font-medium">
              ({kpis.submitted} new, {kpis.inReview} reviewing)
            </span>
          </div>
        </button>

        {/* Generating */}
        <button
          type="button"
          onClick={() => setStatusFilter("generating")}
          className={`text-left p-4 rounded-2xl bg-white border transition-all duration-200 shadow-sm hover:shadow-md ${
            statusFilter === "generating" ? "border-purple-500 ring-2 ring-purple-500/20" : "border-slate-200 hover:border-slate-300"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">AI Generating</span>
            <div className="w-9 h-9 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center">
              <RefreshCw className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-2xl font-black text-purple-600">{kpis.generating}</span>
            <span className="text-xs text-slate-500 font-medium">in worker pipeline</span>
          </div>
        </button>

        {/* Published */}
        <button
          type="button"
          onClick={() => setStatusFilter("published")}
          className={`text-left p-4 rounded-2xl bg-white border transition-all duration-200 shadow-sm hover:shadow-md ${
            statusFilter === "published" ? "border-emerald-500 ring-2 ring-emerald-500/20" : "border-slate-200 hover:border-slate-300"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Published</span>
            <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <CheckCircle2 className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-2xl font-black text-emerald-600">{kpis.published}</span>
            <span className="text-xs text-slate-500 font-medium">active in workspaces</span>
          </div>
        </button>
      </div>

      {/* 2. Filter Bar & Search Toolbar */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
        {/* Status Pills */}
        <div className="flex items-center bg-slate-100/90 border border-slate-200 rounded-xl p-1 text-xs overflow-x-auto scrollbar-none">
          {[
            { id: "all", label: "All", count: kpis.total },
            { id: "needs_review", label: "Needs Review", count: kpis.needsReview },
            { id: "submitted", label: "Submitted", count: kpis.submitted },
            { id: "in_review", label: "In Review", count: kpis.inReview },
            { id: "generating", label: "Generating", count: kpis.generating },
            { id: "published", label: "Published", count: kpis.published },
            { id: "rejected", label: "Rejected", count: kpis.rejected },
          ].map((tab) => {
            const isSelected = statusFilter === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setStatusFilter(tab.id)}
                className={`px-3 py-1.5 rounded-lg font-semibold whitespace-nowrap transition-all flex items-center gap-1.5 ${
                  isSelected
                    ? "bg-white text-indigo-600 shadow-xs border border-slate-200/80 font-bold"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                <span>{tab.label}</span>
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono ${
                    isSelected ? "bg-indigo-50 text-indigo-700 font-bold" : "bg-slate-200/80 text-slate-600"
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Right Search & Refresh Toolbar */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1 md:w-80">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search by title, org, workspace, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-8 py-2 bg-white border border-slate-200 rounded-xl text-xs text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 shadow-xs transition"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing || loading}
            title="Refresh custom course requests"
            className="p-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 rounded-xl transition shadow-xs disabled:opacity-50 flex items-center justify-center shrink-0"
          >
            <RefreshCw className={`w-4 h-4 text-slate-500 ${refreshing ? "animate-spin text-indigo-600" : ""}`} />
          </button>
        </div>
      </div>

      {/* 3. Requests List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="p-6 bg-white border border-slate-200 rounded-2xl animate-pulse space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-slate-200 rounded-xl" />
                <div className="flex-1 space-y-2">
                  <div className="h-5 bg-slate-200 rounded w-1/3" />
                  <div className="h-4 bg-slate-100 rounded w-1/4" />
                </div>
              </div>
              <div className="h-12 bg-slate-50 rounded-xl" />
              <div className="h-8 bg-slate-100 rounded-xl w-3/4" />
            </div>
          ))}
        </div>
      ) : filteredRequests.length === 0 ? (
        <div className="py-16 px-4 text-center bg-white border border-slate-200 rounded-2xl shadow-xs">
          <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center mx-auto mb-3">
            <Sparkles className="w-6 h-6" />
          </div>
          <h3 className="text-base font-bold text-slate-900">No custom course requests found</h3>
          <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
            {searchQuery
              ? `No requests match "${searchQuery}". Try searching by another term or clear the filter.`
              : statusFilter !== "all"
              ? `There are currently no custom course requests with status "${statusFilter.replace("_", " ")}".`
              : "Enterprise workspaces haven't submitted any custom course requests yet."}
          </p>
          {(searchQuery || statusFilter !== "all") && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery("");
                setStatusFilter("all");
              }}
              className="mt-4 px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-semibold rounded-xl transition"
            >
              Reset Filters &amp; Search
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-5">
          {filteredRequests.map((r) => {
            const hasDocs = r.reference_documents && r.reference_documents.length > 0;
            const docCount = r.reference_documents?.length ?? r.reference_document_ids?.length ?? 0;
            const creditBalance = r.organization_credit_balance ?? 0;

            return (
              <div
                key={r.id}
                className="bg-white border border-slate-200/90 rounded-2xl p-5 sm:p-6 shadow-xs hover:border-slate-300 hover:shadow-sm transition-all duration-200 space-y-4"
              >
                {/* Card Header */}
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div className="flex items-start gap-4 min-w-0">
                    {/* Course Thumbnail or Branded Avatar */}
                    {r.image_url ? (
                      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden border border-slate-200 bg-slate-50 shrink-0 shadow-xs">
                        <img
                          src={r.image_url}
                          alt={r.course_title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).style.display = "none";
                          }}
                        />
                      </div>
                    ) : (
                      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl bg-gradient-to-br from-indigo-50 via-purple-50 to-slate-50 border border-indigo-100 flex items-center justify-center text-indigo-600 shrink-0 shadow-xs">
                        <GraduationCap className="w-8 h-8 text-indigo-500/80" />
                      </div>
                    )}

                    {/* Titles and Metadata */}
                    <div className="space-y-1.5 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-base sm:text-lg font-bold text-slate-900 leading-snug">
                          {r.course_title}
                        </h3>
                        {renderStatusBadge(r.status)}
                      </div>

                      {/* Organization & Workspace Pills */}
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg bg-slate-100 text-slate-700 font-medium border border-slate-200">
                          <Building2 className="w-3.5 h-3.5 text-slate-500" />
                          <span>{r.organization_name || `Org #${r.organization_id}`}</span>
                        </span>
                        <span className="text-slate-300 font-semibold">/</span>
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg bg-indigo-50 text-indigo-700 font-medium border border-indigo-100">
                          <Layers className="w-3.5 h-3.5 text-indigo-500" />
                          <span>{r.workspace_name || `Workspace #${r.workspace_id}`}</span>
                        </span>
                        {r.generated_course_id && (
                          <Link
                            to={`/admin/courses/${r.generated_course_id}`}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-purple-50 text-purple-700 hover:bg-purple-100 font-medium border border-purple-200 transition text-[11px]"
                          >
                            <ExternalLink className="w-3 h-3 text-purple-600" />
                            <span>Linked Course #{r.generated_course_id}</span>
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right Header Badges */}
                  <div className="flex flex-wrap sm:flex-col items-start sm:items-end gap-2 shrink-0">
                    {/* Credit Balance Indicator */}
                    <div
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-xl font-mono text-xs font-bold border ${
                        creditBalance > 0
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-rose-50 text-rose-700 border-rose-200"
                      }`}
                    >
                      <Coins className="w-3.5 h-3.5" />
                      <span>{creditBalance} Credits</span>
                    </div>

                    <div className="flex items-center gap-1 text-[11px] text-slate-400 font-medium">
                      <Calendar className="w-3.5 h-3.5 text-slate-400" />
                      <span>Submitted {new Date(r.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>

                {/* Learning Objectives Preview Box */}
                {r.learning_objectives && (
                  <div className="p-3.5 bg-slate-50/70 border border-slate-200/80 rounded-xl space-y-1">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                      <BookOpen className="w-3 h-3 text-indigo-500" />
                      Learning Objectives
                    </span>
                    <p className="text-xs text-slate-700 leading-relaxed line-clamp-2">
                      {r.learning_objectives}
                    </p>
                  </div>
                )}

                {/* Metadata Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs pt-1">
                  {/* Target Audience */}
                  <div className="p-3 bg-slate-50/50 border border-slate-100 rounded-xl">
                    <span className="text-[10px] uppercase font-bold tracking-wider text-slate-400 flex items-center gap-1 mb-1">
                      <Users className="w-3 h-3 text-slate-400" />
                      Target Audience
                    </span>
                    <span className="text-slate-800 font-medium line-clamp-1">
                      {r.target_audience || "Not specified"}
                    </span>
                  </div>

                  {/* Requester */}
                  <div className="p-3 bg-slate-50/50 border border-slate-100 rounded-xl">
                    <span className="text-[10px] uppercase font-bold tracking-wider text-slate-400 flex items-center gap-1 mb-1">
                      <User className="w-3 h-3 text-slate-400" />
                      Requester
                    </span>
                    <div className="min-w-0">
                      <span className="text-slate-800 font-semibold block truncate">
                        {r.requester_name || "Admin"}
                      </span>
                      <span className="text-[11px] font-mono text-slate-500 block truncate">
                        {r.requester_email || "—"}
                      </span>
                    </div>
                  </div>

                  {/* Attached SOPs */}
                  <div className="p-3 bg-slate-50/50 border border-slate-100 rounded-xl">
                    <span className="text-[10px] uppercase font-bold tracking-wider text-slate-400 flex items-center gap-1 mb-1">
                      <FileText className="w-3 h-3 text-indigo-500" />
                      Attached SOPs
                    </span>
                    <span className="text-indigo-700 font-bold block">
                      {docCount} {docCount === 1 ? "document" : "documents"} attached
                    </span>
                  </div>

                  {/* Suggested Modules */}
                  <div className="p-3 bg-slate-50/50 border border-slate-100 rounded-xl">
                    <span className="text-[10px] uppercase font-bold tracking-wider text-slate-400 flex items-center gap-1 mb-1">
                      <Layers className="w-3 h-3 text-purple-500" />
                      Requested Modules
                    </span>
                    <span className="text-purple-700 font-bold block">
                      {r.suggested_modules?.length || 0} curriculum topics
                    </span>
                  </div>
                </div>

                {/* Attached Documents Chips (Direct Preview on click) */}
                {hasDocs && (
                  <div className="pt-2 flex flex-wrap items-center gap-2">
                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                      <FileText className="w-3 h-3 text-indigo-500" />
                      SOP Files:
                    </span>
                    {r.reference_documents!.map((doc) => (
                      <button
                        key={doc.id}
                        type="button"
                        onClick={() => void handlePreviewDocument(r.id, doc)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-50 hover:bg-indigo-50 hover:text-indigo-700 text-slate-700 text-xs font-medium rounded-lg border border-slate-200 transition shadow-2xs group"
                      >
                        <Eye className="w-3 h-3 text-slate-400 group-hover:text-indigo-600 transition" />
                        <span className="truncate max-w-[180px]">{doc.title}</span>
                        <span className="text-[10px] text-slate-400 font-mono uppercase">
                          ({doc.document_type})
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Suggested Modules Preview */}
                {r.suggested_modules && r.suggested_modules.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 pt-1">
                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mr-1">
                      Topics:
                    </span>
                    {r.suggested_modules.slice(0, 4).map((mod, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-0.5 bg-purple-50/70 border border-purple-100 text-purple-800 text-[11px] rounded-md font-medium"
                      >
                        {idx + 1}. {mod}
                      </span>
                    ))}
                    {r.suggested_modules.length > 4 && (
                      <span className="text-[11px] text-slate-400 font-medium">
                        +{r.suggested_modules.length - 4} more
                      </span>
                    )}
                  </div>
                )}

                {/* Admin Feedback Box if present */}
                {r.admin_feedback && (
                  <div className="p-3 bg-amber-50/70 border border-amber-200 rounded-xl text-xs text-amber-900 flex items-start gap-2">
                    <MessageSquare className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <strong className="font-bold text-amber-900">Current Feedback provided to workspace: </strong>
                      <span className="leading-relaxed">{r.admin_feedback}</span>
                    </div>
                  </div>
                )}

                {/* Action Buttons Toolbar */}
                <div className="pt-4 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  {/* Left inspection actions */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => setDetailsModalReq(r)}
                      className="px-3.5 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-bold rounded-xl flex items-center gap-1.5 transition border border-indigo-200 shadow-2xs"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      View Details &amp; Documents
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setActiveCourseReqModal(r);
                        setReviewStatusInput(r.status);
                        setAdminFeedbackInput(r.admin_feedback || "");
                      }}
                      className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl flex items-center gap-1.5 transition border border-slate-200 shadow-2xs"
                    >
                      <Sliders className="w-3.5 h-3.5 text-slate-500" />
                      Review / Notes
                    </button>
                  </div>

                  {/* Right primary fulfillment actions */}
                  <div className="flex items-center gap-2 flex-wrap sm:justify-end">
                    {/* If published, link directly to course */}
                    {r.status === "published" && r.generated_course_id && (
                      <Link
                        to={`/admin/courses/${r.generated_course_id}`}
                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl flex items-center gap-1.5 transition shadow-sm"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        Open Course in Admin
                      </Link>
                    )}

                    {/* Generate with AI button */}
                    {r.status !== "published" && (
                      <button
                        type="button"
                        onClick={() => handleOpenGenerateModal(r)}
                        disabled={actionLoadingId === r.id}
                        className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-xs font-bold rounded-xl flex items-center gap-1.5 transition shadow-sm disabled:opacity-50"
                      >
                        <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                        Generate with AI
                      </button>
                    )}

                    {/* Publish to Workspace button */}
                    {r.status !== "published" && (
                      <button
                        type="button"
                        onClick={() => handlePublishCourse(r.id)}
                        disabled={actionLoadingId === r.id}
                        className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl flex items-center gap-1.5 transition shadow-sm disabled:opacity-50"
                      >
                        {actionLoadingId === r.id ? (
                          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Check className="w-3.5 h-3.5" />
                        )}
                        Publish to Workspace
                      </button>
                    )}

                    {/* Reject button */}
                    {r.status !== "published" && r.status !== "rejected" && (
                      <button
                        type="button"
                        onClick={() => {
                          setRejectModalCourseReq(r);
                          setCourseRejectionFeedback(r.admin_feedback || "");
                        }}
                        className="px-3.5 py-2 bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-bold rounded-xl flex items-center gap-1.5 transition border border-rose-200 shadow-2xs"
                      >
                        <XCircle className="w-3.5 h-3.5" />
                        Reject Request
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* MODAL 1: Details & SOP Documents Modal */}
      <AnimatePresence>
        {detailsModalReq && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 max-w-3xl w-full shadow-2xl space-y-6 max-h-[92vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="flex items-start justify-between pb-4 border-b border-slate-100 gap-4">
                <div className="space-y-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="px-2.5 py-0.5 bg-indigo-50 border border-indigo-100 text-indigo-700 text-xs font-medium rounded-md flex items-center gap-1">
                      <Building2 className="w-3.5 h-3.5 text-indigo-500" />
                      {detailsModalReq.organization_name}
                    </span>
                    <span className="text-slate-300">/</span>
                    <span className="px-2.5 py-0.5 bg-slate-100 border border-slate-200 text-slate-700 text-xs font-medium rounded-md flex items-center gap-1">
                      <Layers className="w-3.5 h-3.5 text-slate-500" />
                      {detailsModalReq.workspace_name}
                    </span>
                    {renderStatusBadge(detailsModalReq.status)}
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                    {detailsModalReq.course_title}
                  </h2>
                  <p className="text-xs text-slate-500">
                    Requested by{" "}
                    <strong className="text-slate-700">{detailsModalReq.requester_name || "Admin"}</strong>{" "}
                    ({detailsModalReq.requester_email}) on{" "}
                    {new Date(detailsModalReq.created_at).toLocaleDateString()}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setDetailsModalReq(null)}
                  className="text-slate-400 hover:text-slate-700 p-2 rounded-xl hover:bg-slate-100 transition shrink-0"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Course Thumbnail Image if present */}
              {detailsModalReq.image_url && (
                <div className="rounded-2xl overflow-hidden border border-slate-200 max-h-56 bg-slate-50 flex items-center justify-center">
                  <img
                    src={detailsModalReq.image_url}
                    alt={detailsModalReq.course_title}
                    className="max-h-56 w-full object-cover"
                  />
                </div>
              )}

              {/* Specs Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-2xl">
                  <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider block mb-1">
                    Target Audience
                  </span>
                  <p className="font-semibold text-slate-800">{detailsModalReq.target_audience}</p>
                </div>
                <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-2xl">
                  <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider block mb-1">
                    Tenant AI Credits
                  </span>
                  <p
                    className={`font-mono font-bold text-sm ${
                      (detailsModalReq.organization_credit_balance ?? 0) > 0
                        ? "text-emerald-600"
                        : "text-rose-600"
                    }`}
                  >
                    {detailsModalReq.organization_credit_balance ?? 0} Credits
                  </p>
                </div>
                <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-2xl">
                  <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider block mb-1">
                    Attached SOPs
                  </span>
                  <p className="font-semibold text-indigo-700">
                    {detailsModalReq.reference_documents?.length ??
                      detailsModalReq.reference_document_ids.length}{" "}
                    files attached
                  </p>
                </div>
              </div>

              {/* Learning Objectives */}
              <div className="space-y-2">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                  <BookOpen className="w-4 h-4 text-indigo-600" />
                  Learning Objectives &amp; Training Scope
                </h4>
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl text-xs text-slate-800 whitespace-pre-wrap leading-relaxed">
                  {detailsModalReq.learning_objectives}
                </div>
              </div>

              {/* Suggested Topics / Modules */}
              {detailsModalReq.suggested_modules && detailsModalReq.suggested_modules.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                    <Layers className="w-4 h-4 text-purple-600" />
                    Requested Curriculum Modules &amp; Topics
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {detailsModalReq.suggested_modules.map((mod, idx) => (
                      <div
                        key={idx}
                        className="p-3 bg-purple-50/50 border border-purple-100 rounded-xl text-xs text-slate-800 flex items-start gap-2"
                      >
                        <span className="w-5 h-5 rounded-full bg-purple-200 text-purple-800 font-bold flex items-center justify-center text-[10px] shrink-0 mt-0.5">
                          {idx + 1}
                        </span>
                        <span className="font-medium">{mod}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Attached Reference Documents */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                    <FileText className="w-4 h-4 text-indigo-600" />
                    Attached Reference Documents &amp; Technical Specs
                  </h4>
                  <span className="text-[11px] text-slate-500 font-medium">
                    {detailsModalReq.reference_documents?.length || 0} document(s)
                  </span>
                </div>

                {detailsModalReq.reference_documents &&
                detailsModalReq.reference_documents.length > 0 ? (
                  <div className="space-y-2.5">
                    {detailsModalReq.reference_documents.map((doc) => (
                      <div
                        key={doc.id}
                        className="p-3.5 bg-slate-50 hover:bg-slate-100/70 border border-slate-200 rounded-2xl transition flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs"
                      >
                        <div className="space-y-1 flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <FileText className="w-4 h-4 text-indigo-600 shrink-0" />
                            <strong className="text-slate-900 truncate">{doc.title}</strong>
                            <span className="px-2 py-0.5 bg-white border border-slate-200 text-slate-600 text-[10px] font-bold rounded-md uppercase">
                              {doc.document_type}
                            </span>
                            {doc.status && (
                              <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-semibold rounded-md">
                                {doc.status}
                              </span>
                            )}
                          </div>
                          {doc.description && (
                            <p className="text-slate-600 text-[11px] pl-6">{doc.description}</p>
                          )}
                          <div className="text-[10px] text-slate-400 pl-6 flex items-center gap-3">
                            <span>Size: {formatFileSize(doc.file_size_bytes)}</span>
                            {doc.chunks_count ? <span>Chunks: {doc.chunks_count}</span> : null}
                          </div>
                        </div>

                        {/* Document Actions */}
                        <div className="flex items-center gap-2 self-end sm:self-center shrink-0">
                          <button
                            type="button"
                            onClick={() => void handlePreviewDocument(detailsModalReq.id, doc)}
                            className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-bold rounded-xl flex items-center gap-1.5 transition border border-indigo-200 shadow-2xs"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            View Content
                          </button>
                          <a
                            href={resolveAdminDocUrl(doc.file_url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1.5 bg-white hover:bg-slate-100 text-slate-700 text-xs font-semibold rounded-xl flex items-center gap-1.5 transition border border-slate-200 shadow-2xs"
                          >
                            <Download className="w-3.5 h-3.5" />
                            Download
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl text-xs text-slate-500 text-center">
                    No documents attached to this custom course request.
                  </div>
                )}
              </div>

              {/* Admin Feedback Display */}
              {detailsModalReq.admin_feedback && (
                <div className="p-3 bg-indigo-50/60 border border-indigo-100 rounded-2xl text-xs text-slate-800 space-y-1">
                  <strong className="text-indigo-800 block text-[11px] uppercase font-bold tracking-wider">
                    Current Super Admin Feedback
                  </strong>
                  <p className="leading-relaxed">{detailsModalReq.admin_feedback}</p>
                </div>
              )}

              {/* Action Buttons Footer */}
              <div className="flex items-center justify-between gap-3 pt-4 border-t border-slate-100 flex-wrap">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const target = detailsModalReq;
                      setDetailsModalReq(null);
                      setActiveCourseReqModal(target);
                      setReviewStatusInput(target.status);
                      setAdminFeedbackInput(target.admin_feedback || "");
                    }}
                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold rounded-xl transition"
                  >
                    Review / Notes
                  </button>
                  {detailsModalReq.status !== "published" &&
                    detailsModalReq.status !== "rejected" && (
                      <button
                        type="button"
                        onClick={() => {
                          const target = detailsModalReq;
                          setDetailsModalReq(null);
                          setRejectModalCourseReq(target);
                          setCourseRejectionFeedback(target.admin_feedback || "");
                        }}
                        className="px-4 py-2 bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-bold rounded-xl border border-rose-200 transition"
                      >
                        Reject Request
                      </button>
                    )}
                </div>

                <div className="flex items-center gap-2">
                  {detailsModalReq.generated_course_id && (
                    <Link
                      to={`/admin/courses/${detailsModalReq.generated_course_id}`}
                      className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold rounded-xl flex items-center gap-1.5 transition"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      View Generated Course
                    </Link>
                  )}
                  {detailsModalReq.status !== "published" && (
                    <button
                      type="button"
                      onClick={() => {
                        const target = detailsModalReq;
                        setDetailsModalReq(null);
                        handleOpenGenerateModal(target);
                      }}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold rounded-xl flex items-center gap-1.5 transition shadow-sm"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      Generate with AI
                    </button>
                  )}
                  {detailsModalReq.status !== "published" && (
                    <button
                      type="button"
                      onClick={() => {
                        const target = detailsModalReq;
                        setDetailsModalReq(null);
                        handlePublishCourse(target.id);
                      }}
                      className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl flex items-center gap-1.5 transition shadow-sm"
                    >
                      <Check className="w-3.5 h-3.5" />
                      Publish to Workspace
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL 2: AI Curriculum Generator & Credit Gate Modal */}
      <AnimatePresence>
        {generateModalReq && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-7 max-w-2xl w-full shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-xl bg-purple-50 border border-purple-100 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-900">
                      AI Curriculum Generator &amp; Credit Gate
                    </h3>
                    <p className="text-[11px] text-slate-500">
                      Curate syllabus for {generateModalReq.organization_name} &bull;{" "}
                      {generateModalReq.workspace_name}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setGenerateModalReq(null)}
                  className="text-slate-400 hover:text-slate-700 p-1.5 rounded-lg"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Credit Status & Request Specs */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-2xl space-y-1 text-xs">
                  <div className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">
                    Target Course
                  </div>
                  <div className="font-bold text-slate-900">{generateModalReq.course_title}</div>
                  <div className="text-slate-500 text-[11px]">
                    Audience: {generateModalReq.target_audience}
                  </div>
                </div>

                <div className="p-3 bg-indigo-50/60 border border-indigo-100 rounded-2xl space-y-1 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase font-bold text-indigo-700 tracking-wider">
                      Tenant Credit Balance
                    </span>
                    <Coins className="w-3.5 h-3.5 text-amber-500" />
                  </div>
                  <div className="text-lg font-black font-mono text-indigo-900">
                    {generateModalReq.organization_credit_balance ?? 0}{" "}
                    <span className="text-xs font-normal text-indigo-600">Credits Available</span>
                  </div>
                  <div className="text-[11px] text-slate-500">
                    Attached SOPs:{" "}
                    <strong>
                      {generateModalReq.reference_documents?.length ??
                        generateModalReq.reference_document_ids.length}{" "}
                      documents
                    </strong>
                  </div>
                </div>
              </div>

              {generateModalReq.reference_documents &&
                generateModalReq.reference_documents.length > 0 && (
                  <div className="p-3 bg-purple-50/60 border border-purple-100 rounded-2xl space-y-1.5 text-xs">
                    <span className="text-[10px] uppercase font-bold text-purple-700 tracking-wider flex items-center gap-1">
                      <FileText className="w-3.5 h-3.5 text-purple-600" />
                      Reference Technical Documents (Primary Grounding Source):
                    </span>
                    <div className="flex flex-wrap gap-2 pt-0.5">
                      {generateModalReq.reference_documents.map((d) => (
                        <button
                          key={d.id}
                          type="button"
                          onClick={() => void handlePreviewDocument(generateModalReq.id, d)}
                          className="px-2.5 py-1 bg-white hover:bg-purple-100 text-purple-900 border border-purple-200 rounded-lg text-[11px] font-medium flex items-center gap-1.5 transition shadow-2xs"
                        >
                          <Eye className="w-3 h-3 text-purple-600" />
                          <span>{d.title}</span>
                          <span className="text-[10px] text-purple-400">({d.document_type})</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

              {generationError && (
                <div className="p-3 bg-rose-50 border border-rose-200 text-rose-800 rounded-2xl text-xs flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                  <span className="leading-relaxed font-semibold">{generationError}</span>
                </div>
              )}

              {/* AI Outline Synthesizer */}
              <div className="space-y-3">
                <div className="p-3 bg-purple-50/60 border border-purple-100 rounded-2xl space-y-2.5">
                  <div className="flex items-center gap-2 text-xs font-bold text-purple-900">
                    <Sliders className="w-3.5 h-3.5 text-purple-600" />
                    <span>Curriculum Structure Parameters</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                        Target Modules
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={10}
                        value={targetModules}
                        onChange={(e) =>
                          setTargetModules(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))
                        }
                        className="w-full px-2.5 py-1.5 bg-white border border-purple-200 rounded-xl text-xs font-mono font-bold text-purple-900 focus:outline-none focus:border-purple-500 shadow-2xs"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                        Lessons / Module
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={8}
                        value={lessonsPerModule}
                        onChange={(e) =>
                          setLessonsPerModule(Math.max(1, Math.min(8, parseInt(e.target.value) || 1)))
                        }
                        className="w-full px-2.5 py-1.5 bg-white border border-purple-200 rounded-xl text-xs font-mono font-bold text-purple-900 focus:outline-none focus:border-purple-500 shadow-2xs"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                        Quizzes / Module
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={3}
                        value={quizzesPerModule}
                        onChange={(e) =>
                          setQuizzesPerModule(Math.max(0, Math.min(3, parseInt(e.target.value) || 0)))
                        }
                        className="w-full px-2.5 py-1.5 bg-white border border-purple-200 rounded-xl text-xs font-mono font-bold text-purple-900 focus:outline-none focus:border-purple-500 shadow-2xs"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                    Course Syllabus Outline
                  </label>
                  <button
                    type="button"
                    onClick={handleSynthesizeOutline}
                    disabled={synthesizingOutline}
                    className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 transition shadow-xs disabled:opacity-50"
                  >
                    {synthesizingOutline ? (
                      <>
                        <RefreshCw className="w-3 h-3 animate-spin" />
                        Synthesizing with AI...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3 h-3 text-amber-300" />
                        Generate AI Outline
                      </>
                    )}
                  </button>
                </div>

                <textarea
                  rows={7}
                  placeholder={`# Level 1: Core Track\n## Module 1: Foundation\n- Lesson 1.1: Core Concepts\n- Lesson 1.2: Hands-on Application\n## Module 2: Applied Operations\n- Lesson 2.1: Incident Triage`}
                  value={outlineText}
                  onChange={(e) => setOutlineText(e.target.value)}
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-mono text-slate-900 focus:bg-white focus:outline-none focus:border-indigo-500 resize-y leading-relaxed"
                />

                {plannedLessons !== null && (
                  <div className="p-3 bg-purple-50/80 border border-purple-200 rounded-2xl flex items-center justify-between text-xs">
                    <div className="flex items-center gap-3">
                      <div>
                        <span className="text-slate-500 block text-[10px] uppercase font-bold">
                          Detected Lessons
                        </span>
                        <strong className="text-slate-900 font-mono">{plannedLessons} lessons</strong>
                      </div>
                      <div className="border-l border-purple-200 pl-3">
                        <span className="text-slate-500 block text-[10px] uppercase font-bold">
                          Estimated Cost
                        </span>
                        <strong className="text-purple-800 font-mono">
                          {creditsRequired} credits
                        </strong>
                      </div>
                    </div>

                    <div>
                      {(generateModalReq.organization_credit_balance ?? 0) >=
                      (creditsRequired ?? 0) ? (
                        <span className="px-2.5 py-1 bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-lg text-[10px] font-bold">
                          Sufficient Balance
                        </span>
                      ) : (
                        <span className="px-2.5 py-1 bg-rose-100 text-rose-800 border border-rose-200 rounded-lg text-[10px] font-bold">
                          Insufficient Credits
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Custom Instructions */}
              <div>
                <label className="block text-xs font-bold text-slate-800 mb-1">
                  Additional Admin Instructions (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. Focus on internal Kubernetes deployment runbooks and high-severity incident escalation..."
                  value={customInstructions}
                  onChange={(e) => setCustomInstructions(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:bg-white focus:outline-none focus:border-indigo-500"
                />
              </div>

              {/* Credit Deduction Toggle */}
              <div className="p-3 border border-slate-200 rounded-2xl flex items-center justify-between text-xs bg-slate-50/50">
                <div>
                  <div className="font-bold text-slate-800">Debit Organization AI Credits</div>
                  <div className="text-[11px] text-slate-500">
                    Reserve {creditsRequired ?? "~10 credits/lesson"} from{" "}
                    {generateModalReq.organization_name}&apos;s balance
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={debitCredits}
                  onChange={(e) => setDebitCredits(e.target.checked)}
                  className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500 cursor-pointer"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-3 border-t border-slate-100 text-xs">
                <Link
                  to="/admin/credits"
                  target="_blank"
                  className="text-indigo-600 hover:text-indigo-800 font-semibold flex items-center gap-1"
                >
                  <Coins className="w-3.5 h-3.5" />
                  Manage/Grant Credits &rarr;
                </Link>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setGenerateModalReq(null)}
                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={
                      actionLoadingId === generateModalReq.id ||
                      (debitCredits &&
                        creditsRequired !== null &&
                        (generateModalReq.organization_credit_balance ?? 0) < creditsRequired)
                    }
                    onClick={handleConfirmGenerate}
                    className="px-5 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold rounded-xl shadow-xs transition disabled:opacity-50 flex items-center gap-1.5"
                  >
                    {actionLoadingId === generateModalReq.id ? (
                      <>
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        Generating Course...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                        Approve &amp; Generate Course
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL 3: Review / Status Modal */}
      <AnimatePresence>
        {activeCourseReqModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white border border-slate-200 rounded-2xl p-6 max-w-md w-full shadow-2xl"
            >
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                  Review Course Request
                </h3>
                <button
                  type="button"
                  onClick={() => setActiveCourseReqModal(null)}
                  className="text-slate-400 hover:text-slate-700 p-1 rounded-lg"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-1.5 mb-4 text-xs">
                <div className="font-bold text-slate-900 text-sm">{activeCourseReqModal.course_title}</div>
                <div className="text-slate-500 text-[11px]">
                  {activeCourseReqModal.organization_name} / {activeCourseReqModal.workspace_name}
                </div>
                <div className="text-slate-600 pt-1 line-clamp-2">
                  {activeCourseReqModal.learning_objectives}
                </div>
              </div>

              <form onSubmit={handleSaveCourseReview} className="space-y-4 text-xs">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1.5">Request Status</label>
                  <select
                    value={reviewStatusInput}
                    onChange={(e) => setReviewStatusInput(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:border-indigo-500 font-medium"
                  >
                    <option value="submitted">Submitted</option>
                    <option value="in_review">In Review</option>
                    <option value="generating">Generating</option>
                    <option value="published">Published</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>

                <div>
                  <label className="block font-semibold text-slate-700 mb-1.5">
                    Admin Feedback &amp; Notes for Workspace
                  </label>
                  <textarea
                    rows={3}
                    placeholder="Provide guidance, required SOP adjustments, or estimated completion time..."
                    value={adminFeedbackInput}
                    onChange={(e) => setAdminFeedbackInput(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-indigo-500 resize-none leading-relaxed"
                  />
                </div>

                <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100">
                  {activeCourseReqModal.status !== "rejected" &&
                  activeCourseReqModal.status !== "published" ? (
                    <button
                      type="button"
                      onClick={() => {
                        const target = activeCourseReqModal;
                        setActiveCourseReqModal(null);
                        setRejectModalCourseReq(target);
                        setCourseRejectionFeedback(adminFeedbackInput || target.admin_feedback || "");
                      }}
                      className="px-3 py-2 bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-bold rounded-xl flex items-center gap-1.5 transition border border-rose-200"
                    >
                      <XCircle className="w-3.5 h-3.5" />
                      Reject Request
                    </button>
                  ) : (
                    <div />
                  )}
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setActiveCourseReqModal(null)}
                      className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-xl transition"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={updatingCourseReq}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-sm transition disabled:opacity-50"
                    >
                      {updatingCourseReq ? "Saving..." : "Save Review"}
                    </button>
                  </div>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL 4: Reject Custom Course Request Modal */}
      <AnimatePresence>
        {rejectModalCourseReq && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white border border-slate-200 rounded-2xl p-6 max-w-md w-full shadow-2xl"
            >
              <div className="flex items-center gap-2 mb-2 text-rose-600">
                <AlertTriangle className="w-5 h-5" />
                <h3 className="text-base font-bold text-slate-900">Reject Custom Course Request</h3>
              </div>
              <p className="text-xs text-slate-600 mb-3">
                Are you sure you want to reject the course request{" "}
                <strong className="text-slate-900">&ldquo;{rejectModalCourseReq.course_title}&rdquo;</strong>{" "}
                for{" "}
                <strong className="text-slate-900">{rejectModalCourseReq.organization_name}</strong>?
              </p>
              <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-2.5 mb-4">
                The Organization Admin will be notified via email with your feedback, allowing them to review and revise their request.
              </div>

              <form onSubmit={handleRejectCourseRequestSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Feedback / Rejection Reason for Organization
                  </label>
                  <textarea
                    rows={3}
                    placeholder="e.g. Please attach relevant internal SOP documents or clarify learning objectives for advanced topics..."
                    value={courseRejectionFeedback}
                    onChange={(e) => setCourseRejectionFeedback(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-rose-500 focus:ring-1 focus:ring-rose-500 resize-none transition"
                  />
                </div>

                <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => {
                      setRejectModalCourseReq(null);
                      setCourseRejectionFeedback("");
                    }}
                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-xl transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={rejectingCourseReq}
                    className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-xl shadow-sm transition disabled:opacity-50 flex items-center gap-1.5"
                  >
                    {rejectingCourseReq ? (
                      <>
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        Rejecting...
                      </>
                    ) : (
                      <>
                        <XCircle className="w-3.5 h-3.5" />
                        Confirm Rejection
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL 5: Document Extracted Content Viewer Modal */}
      <AnimatePresence>
        {previewDocModal && (
          <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-7 max-w-2xl w-full shadow-2xl space-y-4 max-h-[90vh] flex flex-col"
            >
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-indigo-600" />
                    <h3 className="text-base font-bold text-slate-900">{previewDocModal.title}</h3>
                    <span className="px-2 py-0.5 bg-slate-100 border border-slate-200 text-slate-700 text-[10px] font-bold rounded-md uppercase">
                      {previewDocModal.document_type}
                    </span>
                  </div>
                  {previewDocModal.description && (
                    <p className="text-xs text-slate-500">{previewDocModal.description}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setPreviewDocModal(null)}
                  className="text-slate-400 hover:text-slate-700 p-1.5 rounded-lg"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="flex items-center justify-between text-xs text-slate-500 bg-slate-50 p-2.5 rounded-xl border border-slate-200">
                <span>File Size: {formatFileSize(previewDocModal.file_size_bytes)}</span>
                <div className="flex items-center gap-3">
                  {previewDocModal.extracted_text && (
                    <button
                      type="button"
                      onClick={() => handleCopyText(previewDocModal.extracted_text || "")}
                      className="text-indigo-600 hover:text-indigo-800 font-semibold flex items-center gap-1 transition"
                    >
                      {copiedText ? (
                        <>
                          <Check className="w-3.5 h-3.5 text-emerald-600" />
                          <span className="text-emerald-600">Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3.5 h-3.5" />
                          <span>Copy Content</span>
                        </>
                      )}
                    </button>
                  )}
                  <a
                    href={resolveAdminDocUrl(previewDocModal.file_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-800 font-semibold flex items-center gap-1"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Download Original
                  </a>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 bg-slate-900 text-slate-100 rounded-2xl font-mono text-xs leading-relaxed border border-slate-800 whitespace-pre-wrap max-h-[50vh] select-text">
                {previewDocModal.extracted_text || "No extracted text available for this document."}
              </div>

              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  onClick={() => setPreviewDocModal(null)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-xl transition"
                >
                  Close Viewer
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
